import { useCallback, useEffect, useState } from "react";
import { Pause, Play, RefreshCcw, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  active:       { bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.55)", color: "#6ee7b7", label: "Active" },
  paused:       { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.55)", color: "#fbbf24", label: "Paused" },
  past_due:     { bg: "rgba(244,63,94,0.14)",  border: "rgba(244,63,94,0.55)",  color: "#fda4af", label: "Past Due" },
  canceled:     { bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.55)", color: "#cbd5e1", label: "Canceled" },
  open:         { bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.55)", color: "#c4b5fd", label: "Open" },
};

/**
 * 📊 Subscription Lifecycle Control Center
 * -----------------------------------------
 * Grid at the base of Profile / Control Deck. Lists every Nebula-issued
 * recurring subscription (from /v1/nebula/subscriptions), enriched live from
 * Stripe when available. Adjacent Pause / Resume glassmorphic button toggles
 * `stripe.subscriptions.update` with pause_collection.
 */
export function SubscriptionLifecycleGrid() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // invoice_id being paused/resumed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/nebula/subscriptions");
      setRows(Array.isArray(data?.subscriptions) ? data.subscriptions : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load subscriptions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePause = async (row) => {
    if (busy) return;
    const willPause = !row.paused;
    setBusy(row.invoice_id);
    const toastId = toast.loading(willPause ? "⏸ Pausing billing…" : "▶ Resuming billing…");
    try {
      const url = willPause ? "/v1/nebula/subscription/pause" : "/v1/nebula/subscription/resume";
      await api.post(url, { invoice_id: row.invoice_id });
      toast.success(willPause ? "Billing paused — no card charges until resumed" : "Billing resumed · card will charge next cycle", { id: toastId });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pause/Resume failed", { id: toastId });
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (v) => {
    if (!v) return "—";
    try {
      const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
      return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
    } catch { return String(v); }
  };
  const fmt$ = (cents, interval) => {
    const s = `$${((cents || 0) / 100).toFixed(2)}`;
    return interval ? `${s}/${interval === "year" ? "yr" : "mo"}` : s;
  };

  return (
    <div
      data-testid="profile-sub-lifecycle"
      className="mt-6 rounded-2xl p-4 border"
      style={{
        background: "linear-gradient(180deg, rgba(15,7,45,0.72), rgba(30,15,60,0.72))",
        border: "1px solid rgba(139,92,246,0.35)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 0 22px rgba(139,92,246,0.18)",
      }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.28em] text-fuchsia-300">
          <Users size={12} />
          📊 Subscription Lifecycle Control Center
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="profile-sub-refresh"
          className="text-[9px] font-mono uppercase tracking-widest text-slate-400 hover:text-fuchsia-200 inline-flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCcw size={10} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      {rows.length === 0 && !loading ? (
        <div
          data-testid="profile-sub-empty"
          className="rounded-md px-3 py-6 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-dashed border-fuchsia-500/25"
        >
          No recurring Nebula subscriptions yet · flip the Billing Type toggle on the Nebula Vault to mint one
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono" data-testid="profile-sub-table">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-[0.24em] text-slate-400 text-left">
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Next Bill</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.open;
                const paused = r.paused;
                return (
                  <tr
                    key={r.invoice_id}
                    data-testid={`profile-sub-row-${r.invoice_id}`}
                    className="border-t border-fuchsia-500/10 hover:bg-fuchsia-500/5"
                  >
                    <td className="py-2 pr-3 text-slate-200">
                      {r.customer_email || <span className="text-slate-500 italic">pending checkout</span>}
                    </td>
                    <td className="py-2 pr-3 text-white truncate max-w-[220px]" title={r.description}>{r.description}</td>
                    <td className="py-2 pr-3 text-white tabular-nums">{fmt$(r.amount_cents, r.interval)}</td>
                    <td className="py-2 pr-3 text-slate-300">{fmtDate(r.current_period_end)}</td>
                    <td className="py-2 pr-3">
                      <span
                        data-testid={`profile-sub-status-${r.invoice_id}`}
                        className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-md"
                        style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}
                      >
                        {paused ? "Paused" : st.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {r.status !== "canceled" && (
                        <button
                          type="button"
                          onClick={() => togglePause(r)}
                          disabled={busy === r.invoice_id}
                          data-testid={`profile-sub-pause-${r.invoice_id}`}
                          className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-[9px] font-mono uppercase tracking-widest transition-colors disabled:opacity-50 ${paused ? "text-emerald-200 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/25" : "text-amber-200 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/25"}`}
                          title={paused ? "Resume card collection on next cycle" : "Pause billing collection — subscription stays alive"}
                        >
                          {paused ? <Play size={10} /> : <Pause size={10} />}
                          {busy === r.invoice_id ? "…" : (paused ? "Resume" : "⏸️ Pause Billing")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SubscriptionLifecycleGrid;
